(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  root.TimeseekFeatureMechanismInventory = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function decodeHtml(value) {
    return String(value || '')
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function stripTags(value) {
    return decodeHtml(String(value || '').replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
  }

  function extractAttribute(html, name) {
    const escapedName = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = String(html || '').match(new RegExp(`\\b${escapedName}=(['"])([\\s\\S]*?)\\1`, 'i'));
    return match ? decodeHtml(match[2]).trim() : '';
  }

  function extractClassText(html, className) {
    const escapedClass = String(className).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = String(html || '').match(
      new RegExp(`<[^>]*class=(['"])[^'"]*\\b${escapedClass}\\b[^'"]*\\1[^>]*>([\\s\\S]*?)<\\/[^>]+>`, 'i'),
    );
    return match ? stripTags(match[2]) : '';
  }

  function normalizePrice(value) {
    const normalized = String(value || '').replace(/[^0-9.-]/g, '');
    if (!normalized) return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
  }

  function marketRecordToListing(record) {
    const html = String(record?.outerHtml || '');
    const url = String(record?.productUrl || record?.href || extractAttribute(html, 'href')).trim();
    const source = String(record?.source || extractAttribute(html, 'data-ts-source')).trim().toLowerCase();
    const sourceLabel = String(
      record?.sourceLabel || extractAttribute(html, 'data-ts-source-label'),
    ).trim();
    const title =
      extractClassText(html, 'ts-brand-tabs__market-title') ||
      extractAttribute(html, 'aria-label').replace(/の商品ページを開く$/, '') ||
      String(record?.searchText || '').trim();
    const imageMatch = html.match(/<img\b[^>]*>/i);
    const image = imageMatch ? extractAttribute(imageMatch[0], 'src') : '';
    const reference = String(
      record?.rawReference || extractAttribute(html, 'data-ts-reference'),
    ).trim();
    const sourceType = ['rakuten', 'yahoo', 'yahoo_shopping'].includes(source) ? 'pr' : 'market';

    if (!url || !title) return null;
    return {
      id: String(record?.listingKey || url),
      title,
      brand: extractAttribute(html, 'data-ts-brand'),
      model: extractAttribute(html, 'data-ts-model-label') || extractAttribute(html, 'data-ts-model'),
      reference,
      price: normalizePrice(extractAttribute(html, 'data-ts-price')),
      image,
      url,
      source_type: sourceType,
      shop_name: sourceLabel || 'マーケット',
      data_confidence: 'high',
    };
  }

  function mechanismEntries(indexPayload, handle) {
    const normalizedHandle = String(handle || '').trim().toLowerCase();
    const definition = indexPayload?.mechanisms?.[normalizedHandle] || {};
    const collections = [
      ...(Array.isArray(definition.models) ? definition.models : []),
      ...(Array.isArray(definition.references) ? definition.references : []),
    ];
    return collections
      .map((collection) => ({
        handle: String(collection?.handle || '').trim(),
        url: String(collection?.url || '').trim(),
        title: String(collection?.title || '').trim(),
      }))
      .filter((collection) => collection.handle && collection.url);
  }

  async function mapWithConcurrency(items, concurrency, iteratee) {
    const queue = items.map((item, index) => ({ item, index }));
    const results = new Array(items.length);
    const workers = Array.from(
      { length: Math.min(Math.max(1, Number(concurrency) || 1), queue.length || 1) },
      async () => {
        while (queue.length > 0) {
          const payload = queue.shift();
          results[payload.index] = await iteratee(payload.item, payload.index);
        }
      },
    );
    await Promise.all(workers);
    return results;
  }

  function roundRobinRecords(recordGroups, limit) {
    const groups = recordGroups.map((records) => (Array.isArray(records) ? records : []));
    const selected = [];
    const seen = new Set();
    let rowIndex = 0;
    let addedInRound = true;

    while (selected.length < limit && addedInRound) {
      addedInRound = false;
      for (const group of groups) {
        const record = group[rowIndex];
        if (!record) continue;
        addedInRound = true;
        const key = String(record.listingKey || record.productUrl || record.href || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        selected.push(record);
        if (selected.length >= limit) break;
      }
      rowIndex += 1;
    }
    return selected;
  }

  async function loadMechanismMarketInventory(options) {
    const fetchImpl = options?.fetchImpl || globalThis.fetch;
    const marketCatalog = options?.marketCatalog || globalThis.TimeseekMarketCatalog;
    if (typeof fetchImpl !== 'function' || typeof marketCatalog?.loadMarketCatalog !== 'function') {
      throw new Error('Mechanism inventory fallback dependencies are unavailable.');
    }

    const indexResponse = await fetchImpl(options.indexUrl, { credentials: 'same-origin' });
    if (!indexResponse?.ok) {
      throw new Error(`Mechanism inventory index request failed: ${indexResponse?.status || 'unknown'}`);
    }
    const entries = mechanismEntries(await indexResponse.json(), options.handle);
    if (entries.length === 0) {
      return { items: [], entries: [], stats: { requestedEntries: 0 } };
    }

    const limit = Math.max(1, Number(options.limit) || 48);
    const fetchHtml = async (url) => {
      const response = await fetchImpl(url, { credentials: 'same-origin' });
      if (!response.ok) {
        const error = new Error(`Mechanism market collection request failed: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.text();
    };
    const results = await mapWithConcurrency(entries, 2, (entry) =>
      marketCatalog.loadMarketCatalog({
        entries: [entry],
        fetchHtml,
        concurrency: 1,
        maxRetries: 2,
        retryDelayMs: 300,
        recoveryDelayMs: 400,
        logger: options.logger || globalThis.console,
      }),
    );
    const selectedRecords = roundRobinRecords(
      results.map((result) => result?.records || []),
      limit,
    );
    const items = selectedRecords
      .map(marketRecordToListing)
      .filter(Boolean)
      .slice(0, limit);
    const numericStatKeys = [
      'fetchedPages',
      'mergedCards',
      'duplicateCards',
      'skippedNonMarketCards',
      'missingReferenceCount',
      'invalidReferenceCount',
      'failedPages',
      'retryableFailedPages',
      'nonRetryableFailedPages',
      'unresolvedRetryableEntries',
    ];
    const stats = { requestedEntries: entries.length };
    numericStatKeys.forEach((key) => {
      stats[key] = results.reduce((sum, result) => sum + Number(result?.stats?.[key] || 0), 0);
    });
    return { items, entries, stats };
  }

  return {
    extractAttribute,
    extractClassText,
    marketRecordToListing,
    mechanismEntries,
    roundRobinRecords,
    loadMechanismMarketInventory,
  };
});
